import i18n, {useTranslation} from '@/translations/i18n'
import {StyledColorSquare, StyledSelectCell, StyledWrapper} from './style'
import {Helmet} from 'react-helmet'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {PageHeading} from '@/components/ui/page-atoms/PageAtoms'
import {FiltersBar} from '@/components/ui/filters-bar/FiltersBar'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {TableInputSearch} from '@/components/commons/table-input-search/TableInputSearch'
import {ChangeEvent, useRef, useState} from 'react'
import {
    columnVisibilitySearchParamsToPayload,
    columnVisibilityToSearchParams,
    debounce,
    filtersParamToPayload,
    getIsRowSelected,
    sortParamToPayload
} from '@/utilities/helpers'
import {SelectedRowsText} from '@/components/commons/selected-rows-text/SelectedRowsText'
import {ColumnVisibilityMenu} from '@/components/commons/columns-visibility-menu/ColumnVisibilityMenu'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {AlertCircleIcon, AlertTriangleIcon, Download01Icon} from '@/components/ui/icon/Icon'
import {Table} from '@/components/commons/table/Table'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {Button} from '@/components/ui/button/Button'
import {TableBodyCell, TableBodyCellProps, TableBodyCellText} from '@/components/ui/table-atoms/TableAtoms'
import {TableBodyRow} from '@/components/ui/table-body-row/TableBodyRow'
import {SelectRowCheckbox} from '@/components/ui/select-row-checkbox/SelectRowCheckbox'
import {SelectColumnHeader} from '@/components/ui/select-column-header/SelectColumnHeader'
import {MoreActionsCell} from '@/components/ui/more-actions-cell/MoreActionsCell'
import {Access, AccessesColumn, AccessesSearchParams} from '@/features/accesses/types'
import {useAccessesTable} from '@/features/accesses/queries/useAccessesTable'
import {formatLocaleDate} from '@/dayjs'
import {
    accessesStatusToLabel,
    accessTypeToLabel,
    accessesColumnToLabel as columnNameToLabel
} from '@/features/accesses/utils'
import {Text} from '@/components/ui/text/Text'
import {accessGroupNameEnumToLabel, eventTypeMapToLabel} from '@/features/agenda/utils'
import {AccessesOtherActionMenu} from '@/features/accesses/components/accesses-other-action-menu/AccessesOtherActionMenu'
import {projectAssistiveRegimeToLabel, projectSectorToLabel, projectStatusToLabel} from '@/features/projects/utils'
import {AddAccessAbsenceMenu} from '@/features/accesses/components/add-access-absence-menu/AddAccessAbsenceMenu'
import {ExportRiaDialog} from '@/features/accesses/components/export-ria-dialog/ExportRiaDialog'
import {GenerateNrAbsencesDialog} from '@/features/accesses/components/generate-nr-absences-dialog/GenerateNrAbsencesDialog'
import {SortingMenu} from '@/components/commons/sorting-menu/SortingMenu'
import {TableFilters} from '@/components/commons/table-filters/TableFilters'
import {AccessesTableDashboard} from '@/features/accesses/components/accesses-table-dashboard/AccessesTableDashboard'
import {ViewSelect} from '@/features/views/views-select/ViewSelect'
import {useIsMutating, useMutationState} from '@tanstack/react-query'
import {MUTATION_KEYS} from '@/queryClient'
import {httpExportAccesses, HttpValidateAccessesOptions} from '@/features/accesses/http/accesses.http'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useTheme} from 'styled-components'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {ExportModal} from '@/components/commons/export-modal/ExportModal'
import {DiscardAccessButton} from '@/features/accesses/components/discard-access-button/DiscardAccessButton'
import {DateRangeFilter} from '@/components/commons/date-range-filter/DateRangeFilter'
import {DateRangeFilterEnum} from '@/types'

const makeColumnsMap = () =>
    [
        {id: AccessesColumn.enum.date, label: i18n.t(columnNameToLabel[AccessesColumn.enum.date])},
        {id: AccessesColumn.enum.type, label: i18n.t(columnNameToLabel[AccessesColumn.enum.type])},
        {id: AccessesColumn.enum.sector, label: i18n.t(columnNameToLabel[AccessesColumn.enum.sector])},
        {id: AccessesColumn.enum.isRemote, label: i18n.t(columnNameToLabel[AccessesColumn.enum.isRemote])},
        {
            id: AccessesColumn.enum.assistiveRegime,
            label: i18n.t(columnNameToLabel[AccessesColumn.enum.assistiveRegime])
        },
        {id: AccessesColumn.enum.groupName, label: i18n.t(columnNameToLabel[AccessesColumn.enum.groupName])},
        {id: AccessesColumn.enum.accessType, label: i18n.t(columnNameToLabel[AccessesColumn.enum.accessType])},
        {
            id: AccessesColumn.enum.consultantId,
            label: i18n.t(columnNameToLabel[AccessesColumn.enum.consultantId])
        },
        {id: AccessesColumn.enum.status, label: i18n.t(columnNameToLabel[AccessesColumn.enum.status])},
        {id: AccessesColumn.enum.report, label: i18n.t(columnNameToLabel[AccessesColumn.enum.report])},
        {id: AccessesColumn.enum.aslId, label: i18n.t(columnNameToLabel[AccessesColumn.enum.aslId])},
        {id: AccessesColumn.enum.rateCode, label: i18n.t(columnNameToLabel[AccessesColumn.enum.rateCode])},
        {
            id: AccessesColumn.enum.remappedSiarAccessType,
            label: i18n.t(columnNameToLabel[AccessesColumn.enum.remappedSiarAccessType])
        },
        {id: AccessesColumn.enum.access_100, label: i18n.t(columnNameToLabel[AccessesColumn.enum.access_100])},
        {
            id: AccessesColumn.enum.absence_20,
            label: i18n.t(columnNameToLabel[AccessesColumn.enum.absence_20], {value: '20%'})
        },
        {
            id: AccessesColumn.enum.absence_50,
            label: i18n.t(columnNameToLabel[AccessesColumn.enum.absence_50], {value: '50%'})
        },
        {
            id: AccessesColumn.enum.absence_nr,
            label: i18n.t(columnNameToLabel[AccessesColumn.enum.absence_nr], {value: 'NR'})
        },
        {
            id: AccessesColumn.enum.communicatedOnTime,
            label: i18n.t(columnNameToLabel[AccessesColumn.enum.communicatedOnTime])
        },
        {
            id: AccessesColumn.enum.patientId,
            label: i18n.t(columnNameToLabel[AccessesColumn.enum.patientId])
        },
        {id: AccessesColumn.enum.projectCode, label: i18n.t(columnNameToLabel[AccessesColumn.enum.projectCode])},
        {
            id: AccessesColumn.enum.projectStartDate,
            label: i18n.t(columnNameToLabel[AccessesColumn.enum.projectStartDate])
        },
        {
            id: AccessesColumn.enum.projectExpirationDate,
            label: i18n.t(columnNameToLabel[AccessesColumn.enum.projectExpirationDate])
        },
        {id: AccessesColumn.enum.projectStatus, label: i18n.t(columnNameToLabel[AccessesColumn.enum.projectStatus])}
    ] as const satisfies {id: AccessesColumn; label: string}[]

export const AccessesTable = () => {
    const {t} = useTranslation()
    const columnsMap = makeColumnsMap()
    const inputSearchRef = useRef<HTMLInputElement | null>(null)
    const {searchParams, setSearchParams} = useParsedSearchParams(AccessesSearchParams)
    const accessesQuery = useAccessesTable(
        {
            limit: searchParams.limit,
            page: searchParams.page,
            search: searchParams.search,
            startDate: searchParams.startDate,
            endDate: searchParams.endDate
        },
        {
            filters: filtersParamToPayload(searchParams.filters)?.filters,
            sorts: sortParamToPayload(searchParams.sorts)
        }
    )
    const [isSelectAll, setIsSelectAll] = useState(false)
    const [unselectedAccessesIds, setUnselectedAccessesIds] = useState<string[]>([])
    const [selectedAccessesIds, setSelectedAccessesIds] = useState<string[]>([])
    const selectedAccessesCount = isSelectAll
        ? accessesQuery.persistedPagination.total - unselectedAccessesIds.length
        : selectedAccessesIds.length
    const onSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearchParams({search: event.target.value || undefined, page: 1})
    }, 1000)
    const resetSelectionStates = () => {
        setIsSelectAll(false)
        setSelectedAccessesIds([])
        setUnselectedAccessesIds([])
    }

    const columnVisibility =
        columnVisibilitySearchParamsToPayload(AccessesColumn, searchParams.columnVisibility) ??
        Object.fromEntries(AccessesColumn.options.map(column => [column, true]))

    const {palette} = useTheme()

    const handleApplyDateRange = (values: {startDate: string; endDate: string; range: DateRangeFilterEnum}) => {
        setSearchParams(values)
        resetSelectionStates()
    }

    return (
        <StyledWrapper>
            <Helmet title={t('accesses:meta_title')} />
            <PageHeading direction="column" gap={6}>
                <Flexbox fullWidth justify="space-between" align="center" gap={2}>
                    <h2>{t('accesses:title')}</h2>
                    <Flexbox gap={2.5}>
                        {/* Renders itself only when the period filter is set on a month already over. */}
                        <GenerateNrAbsencesDialog
                            period={{startDate: searchParams.startDate, range: searchParams.range}}
                        />
                        <ExportRiaDialog />
                        <AddAccessAbsenceMenu />
                    </Flexbox>
                </Flexbox>
                <AccessesTableDashboard />
            </PageHeading>
            <FiltersBar>
                <Flexbox align="center" gap={2}>
                    <TableInputSearch
                        ref={inputSearchRef}
                        defaultValue={searchParams.search}
                        onChange={onSearch}
                        placeholder={t('commons:search_operator')}
                    />
                    {selectedAccessesCount >= 1 && (
                        <>
                            <SelectedRowsText count={selectedAccessesCount} />
                            <Button variant="ghost" size="sm" onClick={resetSelectionStates}>
                                {t('commons:cancel')}
                            </Button>
                            <ExportModal
                                trigger={
                                    <Button variant="ghost" size="sm">
                                        <Download01Icon />
                                        {t('commons:export')}
                                    </Button>
                                }
                                asyncExport
                                fileName="Accessi"
                                promise={({extension, mode}) => {
                                    return httpExportAccesses({
                                        params: {
                                            startDate: searchParams.startDate ?? '',
                                            endDate: searchParams.endDate ?? ''
                                        },
                                        payload: {
                                            selectedAccessIds: selectedAccessesIds,
                                            exceptedAccessIds: unselectedAccessesIds,
                                            type: extension,
                                            filters: filtersParamToPayload(searchParams.filters)?.filters,
                                            fields:
                                                mode == 'view'
                                                    ? Object.keys(columnVisibility).filter(
                                                          column =>
                                                              columnVisibility[column] &&
                                                              column !== 'select' &&
                                                              column !== 'moreActions'
                                                      )
                                                    : AccessesColumn.exclude(['select', 'moreActions']).options,
                                            sorts: sortParamToPayload(searchParams.sorts)
                                        }
                                    })
                                }}
                                selectedCount={selectedAccessesCount}
                                allowedExtensions={['xlsx', 'xls', 'csv']}
                            />
                            <DiscardAccessButton
                                accessesToDiscard={
                                    accessesQuery.data?.data.filter(dataItem =>
                                        getIsRowSelected({
                                            dataItem,
                                            selectedIds: selectedAccessesIds,
                                            unselectedIds: unselectedAccessesIds,
                                            isSelectAll
                                        })
                                    ) ?? []
                                }
                                onSuccess={() => {
                                    resetSelectionStates()
                                }}
                            />
                        </>
                    )}
                    {selectedAccessesCount == 0 && (
                        <>
                            <ViewSelect entity="accesses" searchInputRef={inputSearchRef} fullWidth={false} />
                            <DateRangeFilter
                                size="sm"
                                values={
                                    searchParams.startDate && searchParams.endDate && searchParams.range
                                        ? {
                                              startDate: searchParams.startDate,
                                              endDate: searchParams.endDate,
                                              range: searchParams.range
                                          }
                                        : undefined
                                }
                                onApply={handleApplyDateRange}
                                visibleOptions={[DateRangeFilterEnum.Enum.month, DateRangeFilterEnum.Enum.year]}
                            />
                        </>
                    )}
                </Flexbox>

                {selectedAccessesCount == 0 && (
                    <Flexbox style={{flexShrink: 0}} align="center" gap={2}>
                        <TableFilters
                            onApply={filters => {
                                setSearchParams({filters: JSON.stringify(filters), page: 1})
                                resetSelectionStates()
                            }}
                            filtersParams={searchParams.filters}
                            entity="accesses"
                            availableFilters={[
                                {key: AccessesColumn.enum.type, label: columnNameToLabel[AccessesColumn.enum.type]},
                                {
                                    key: AccessesColumn.enum.sector,
                                    label: columnNameToLabel[AccessesColumn.enum.sector]
                                },
                                {
                                    key: AccessesColumn.enum.isRemote,
                                    label: columnNameToLabel[AccessesColumn.enum.isRemote]
                                },
                                {
                                    key: AccessesColumn.enum.assistiveRegime,
                                    label: columnNameToLabel[AccessesColumn.enum.assistiveRegime]
                                },
                                {
                                    key: AccessesColumn.enum.groupName,
                                    label: columnNameToLabel[AccessesColumn.enum.groupName]
                                },
                                {
                                    key: AccessesColumn.enum.accessType,
                                    label: columnNameToLabel[AccessesColumn.enum.accessType]
                                },
                                {
                                    key: AccessesColumn.enum.consultantId,
                                    label: columnNameToLabel[AccessesColumn.enum.consultantId]
                                },
                                {
                                    key: AccessesColumn.enum.status,
                                    label: columnNameToLabel[AccessesColumn.enum.status]
                                },
                                {
                                    key: AccessesColumn.enum.report,
                                    label: columnNameToLabel[AccessesColumn.enum.report]
                                },
                                {
                                    key: AccessesColumn.enum.aslId,
                                    label: columnNameToLabel[AccessesColumn.enum.aslId]
                                },
                                {
                                    key: AccessesColumn.enum.rateCode,
                                    label: columnNameToLabel[AccessesColumn.enum.rateCode]
                                },
                                {
                                    key: AccessesColumn.enum.remappedSiarAccessType,
                                    label: columnNameToLabel[AccessesColumn.enum.remappedSiarAccessType]
                                },
                                {
                                    key: AccessesColumn.enum.access_100,
                                    label: columnNameToLabel[AccessesColumn.enum.access_100]
                                },
                                {
                                    key: AccessesColumn.enum.absence_20,
                                    label: columnNameToLabel[AccessesColumn.enum.absence_20]
                                },
                                {
                                    key: AccessesColumn.enum.absence_50,
                                    label: columnNameToLabel[AccessesColumn.enum.absence_50]
                                },
                                {
                                    key: AccessesColumn.enum.absence_nr,
                                    label: columnNameToLabel[AccessesColumn.enum.absence_nr]
                                },
                                {
                                    key: AccessesColumn.enum.communicatedOnTime,
                                    label: columnNameToLabel[AccessesColumn.enum.communicatedOnTime]
                                },
                                {
                                    key: AccessesColumn.enum.patientId,
                                    label: columnNameToLabel[AccessesColumn.enum.patientId]
                                },
                                {
                                    key: AccessesColumn.enum.projectCode,
                                    label: columnNameToLabel[AccessesColumn.enum.projectCode]
                                },
                                {
                                    key: AccessesColumn.enum.projectStartDate,
                                    label: columnNameToLabel[AccessesColumn.enum.projectStartDate]
                                },
                                {
                                    key: AccessesColumn.enum.projectExpirationDate,
                                    label: columnNameToLabel[AccessesColumn.enum.projectExpirationDate]
                                },
                                {
                                    key: AccessesColumn.enum.projectStatus,
                                    label: columnNameToLabel[AccessesColumn.enum.projectStatus]
                                }
                            ]}
                        />
                        <SortingMenu
                            columns={columnsMap}
                            order={sortParamToPayload(searchParams.sorts) ?? []}
                            setOrder={sorts => {
                                setSearchParams({sorts: JSON.stringify(sorts)})
                                resetSelectionStates()
                            }}
                        />
                        <ColumnVisibilityMenu
                            columns={columnsMap}
                            columnVisibility={columnVisibility}
                            onSubmit={columnVisibility =>
                                setSearchParams({columnVisibility: columnVisibilityToSearchParams(columnVisibility)})
                            }
                        />
                    </Flexbox>
                )}
            </FiltersBar>
            {accessesQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : (
                <Table
                    emptyStateComponent={
                        <EmptyState
                            title={t('accesses:no_accesses_title')}
                            subtitle={t('accesses:no_accesses_description')}
                        >
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    setSearchParams({
                                        search: undefined,
                                        filters: undefined,
                                        view: undefined,
                                        page: 1,
                                        startDate: undefined,
                                        endDate: undefined
                                    })
                                    if (inputSearchRef.current) {
                                        inputSearchRef.current.value = ''
                                    }
                                }}
                            >
                                {t('accesses:no_accesses_cta')}
                            </Button>
                        </EmptyState>
                    }
                    rowComponent={({tableBodyRowProps, index}) => (
                        <TableBodyRow key={index} {...tableBodyRowProps} style={{cursor: 'default'}} />
                    )}
                    pagination={{
                        ...accessesQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: searchParams.limit,
                        onChangePage: page => setSearchParams({page}),
                        onChangeLimit: limit => {
                            resetSelectionStates()
                            setSearchParams({limit, page: 1})
                        }
                    }}
                    columnPinning={{
                        left: [AccessesColumn.enum.select],
                        right: [AccessesColumn.enum.moreActions]
                    }}
                    columnVisibility={columnVisibility}
                    getIsRowSelected={({dataItem}) =>
                        getIsRowSelected({
                            dataItem,
                            selectedIds: selectedAccessesIds,
                            unselectedIds: unselectedAccessesIds,
                            isSelectAll
                        })
                    }
                    isLoading={accessesQuery.isPending}
                    data={accessesQuery.data?.data ?? []}
                    columns={[
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledSelectCell>
                                        <SelectRowCheckbox
                                            isSelectAll={isSelectAll}
                                            setSelectedIds={setSelectedAccessesIds}
                                            getIsRowSelected={({dataItem}) =>
                                                getIsRowSelected({
                                                    dataItem,
                                                    selectedIds: selectedAccessesIds,
                                                    unselectedIds: unselectedAccessesIds,
                                                    isSelectAll
                                                })
                                            }
                                            dataItem={dataItem}
                                            setUnselectedIds={setUnselectedAccessesIds}
                                        />
                                    </StyledSelectCell>
                                </TableBodyCell>
                            ),
                            header: () => (
                                <SelectColumnHeader
                                    currentPageIds={accessesQuery.data?.data.map(access => access.id) ?? []}
                                    isSelectAll={isSelectAll}
                                    selectedIds={selectedAccessesIds}
                                    setIsSelectAll={setIsSelectAll}
                                    unselectedIds={unselectedAccessesIds}
                                    setUnselectedIds={setUnselectedAccessesIds}
                                    setSelectedIds={setSelectedAccessesIds}
                                    selectedCount={selectedAccessesCount}
                                    paginationTotal={accessesQuery.persistedPagination.total}
                                />
                            ),
                            id: AccessesColumn.enum.select,
                            size: () => 64,
                            minSize: () => 24
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {formatLocaleDate(dataItem.date ?? dataItem.event.date, 'll')}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('commons:date'),
                            id: AccessesColumn.enum.date,
                            size: () => 140,
                            minSize: () => 100
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{t(accessTypeToLabel[dataItem.type])}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('commons:type'),
                            id: AccessesColumn.enum.type,
                            size: () => 140,
                            minSize: () => 100
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem?.sector ? t(projectSectorToLabel[dataItem.sector]) : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('accesses:mode'),
                            id: AccessesColumn.enum.sector,
                            size: () => 140,
                            minSize: () => 100
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.event.isRemote ? t('commons:yes') : t('commons:no')}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('accesses:remote'),
                            id: AccessesColumn.enum.isRemote,
                            size: () => 100,
                            minSize: () => 60
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.project?.assistiveRegime
                                            ? t(projectAssistiveRegimeToLabel[dataItem.project?.assistiveRegime])
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('commons:care_sub_regime'),
                            id: AccessesColumn.enum.assistiveRegime,
                            size: () => 210,
                            minSize: () => 160
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                const groupName = dataItem.event.eventConsultants?.[0]?.accessType?.groupName
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>
                                            {groupName ? t(accessGroupNameEnumToLabel[groupName]) : '-'}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t('accesses:group'),
                            id: AccessesColumn.enum.groupName,
                            size: () => 140,
                            minSize: () => 100
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                const accessType = dataItem.event.eventConsultants?.[0]?.accessType?.accessType
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>
                                            {accessType ? t(eventTypeMapToLabel[accessType]) : '-'}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t('accesses:access_type'),
                            id: AccessesColumn.enum.accessType,
                            maxSize: () => 320,
                            size: () => 200,
                            minSize: () => 160
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <Flexbox direction="column" align="stretch" fullWidth style={{minWidth: 0}}>
                                            <Text
                                                title={dataItem.consultant?.fullName}
                                                size="textSm"
                                                palette="neutral"
                                                shade="950"
                                                style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {dataItem.consultant?.fullName ?? '-'}
                                            </Text>
                                            {dataItem.consultant?.specialization && (
                                                <Text
                                                    title={dataItem.consultant?.specialization}
                                                    size="textSm"
                                                    palette="neutral"
                                                    shade="500"
                                                    style={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {dataItem.consultant?.specialization}
                                                </Text>
                                            )}
                                        </Flexbox>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t('commons:operator'),
                            id: AccessesColumn.enum.consultantId,
                            maxSize: () => 320,
                            size: () => 200,
                            minSize: () => 160
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <AccessesStatusRow forwardedProps={forwardedProps} dataItem={dataItem} />
                            ),
                            header: () => t('accesses:validation'),
                            id: AccessesColumn.enum.status,
                            size: () => 140,
                            minSize: () => 100
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.medicalRecordReport ? t('commons:yes') : t('commons:no')}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('accesses:report'),
                            id: AccessesColumn.enum.report,
                            size: () => 120,
                            minSize: () => 80
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.patient?.residenceAslDistrict?.aslName
                                            ? `${dataItem.patient?.residenceAslDistrict?.aslName} - ${dataItem.patient?.residenceAslDistrict?.name}`
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('commons:asl'),
                            id: AccessesColumn.enum.aslId,
                            maxSize: () => 320,
                            size: () => 200,
                            minSize: () => 160
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.rateCode ?? '-'}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('accesses:rate'),
                            id: AccessesColumn.enum.rateCode,
                            size: () => 80,
                            minSize: () => 40
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText title={dataItem.remappedSiarAccessType ?? undefined}>
                                            {dataItem.remappedSiarAccessType ?? '-'}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t('accesses:siar_accesses'),
                            id: AccessesColumn.enum.remappedSiarAccessType,
                            maxSize: () => 320,
                            size: () => 200,
                            minSize: () => 160
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.payOutcome == 'access_100' ? '1' : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('accesses:accesses_100'),
                            id: AccessesColumn.enum.access_100,
                            maxSize: () => 320,
                            size: () => 120,
                            minSize: () => 80
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.payOutcome == 'absence_20' ? '1' : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('accesses:absence_20'),
                            id: AccessesColumn.enum.absence_20,
                            maxSize: () => 320,
                            size: () => 120,
                            minSize: () => 80
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.payOutcome == 'absence_50' ? '1' : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('accesses:absence_50'),
                            id: AccessesColumn.enum.absence_50,
                            maxSize: () => 320,
                            size: () => 120,
                            minSize: () => 80
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.payOutcome == 'absence_nr' ? '1' : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('accesses:absence_nr'),
                            id: AccessesColumn.enum.absence_nr,
                            maxSize: () => 320,
                            size: () => 120,
                            minSize: () => 80
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.event.communicatedOnTime !== null ? (
                                            dataItem.event.communicatedOnTime ? (
                                                t('commons:over_48_hours')
                                            ) : (
                                                t('commons:within_48_hours')
                                            )
                                        ) : dataItem.type == 'absence' ? (
                                            <Tooltip variant="light" content={t('accesses:absence_not_communicated')}>
                                                <AlertCircleIcon color={palette.orange['500']} size={15} />
                                            </Tooltip>
                                        ) : (
                                            '-'
                                        )}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('accesses:absence_type'),
                            id: AccessesColumn.enum.communicatedOnTime,
                            maxSize: () => 320,
                            size: () => 120,
                            minSize: () => 80
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                const patientFullName = dataItem.event.eventPatients?.find(
                                    ep => ep.patientId === dataItem.patientId
                                )?.patient?.fullName
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText title={patientFullName}>
                                            {patientFullName ?? '-'}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t('commons:patient'),
                            id: AccessesColumn.enum.patientId,
                            maxSize: () => 320,
                            size: () => 160,
                            minSize: () => 120
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.project?.code ?? '-'}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('commons:pri_n'),
                            id: AccessesColumn.enum.projectCode,
                            size: () => 100,
                            minSize: () => 60
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.project?.startDate
                                            ? formatLocaleDate(dataItem.project.startDate, 'll')
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('accesses:pri_start_date'),
                            id: AccessesColumn.enum.projectStartDate,
                            size: () => 100,
                            minSize: () => 60
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.project?.expirationDate
                                            ? formatLocaleDate(dataItem.project.expirationDate, 'll')
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('accesses:pri_expiration_date'),
                            id: AccessesColumn.enum.projectExpirationDate,
                            size: () => 120,
                            minSize: () => 80
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.project?.status
                                            ? t(projectStatusToLabel[dataItem.project.status])
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t('accesses:pri_status'),
                            id: AccessesColumn.enum.projectStatus,
                            size: () => 120,
                            minSize: () => 80
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <MoreActionsCell {...forwardedProps}>
                                    <AccessesOtherActionMenu
                                        access={dataItem}
                                        onDeleteSuccess={() => {
                                            setSelectedAccessesIds(prev => prev.filter(id => id != dataItem.id))
                                            setUnselectedAccessesIds(prev => prev.filter(id => id != dataItem.id))
                                        }}
                                    />
                                </MoreActionsCell>
                            ),
                            header: () => undefined,
                            id: AccessesColumn.enum.moreActions,
                            size: () => 56
                        }
                    ]}
                />
            )}
        </StyledWrapper>
    )
}

export const AccessesStatusRow = ({
    forwardedProps,
    dataItem
}: {
    forwardedProps: TableBodyCellProps
    dataItem: Access
}) => {
    const {t} = useTranslation()
    const validatingAccessesMutation = useMutationState({
        filters: {
            mutationKey: [MUTATION_KEYS.VALIDATE_ACCESS],
            status: 'pending'
        },
        select: mutation => mutation.state.variables as HttpValidateAccessesOptions['payload']
    })

    const isInvalidating = useIsMutating({mutationKey: [MUTATION_KEYS.UPDATE_ACCESS, dataItem.id]})

    return (
        <TableBodyCell {...forwardedProps}>
            {validatingAccessesMutation.some(mutation => mutation.accessIds.includes(dataItem.id)) ||
            !!isInvalidating ? (
                <Skeleton height={32} width={116} />
            ) : (
                <Flexbox align="center" gap={1}>
                    <StyledColorSquare $status={dataItem.status} />
                    <TableBodyCellText>{t(accessesStatusToLabel[dataItem.status])}</TableBodyCellText>
                </Flexbox>
            )}
        </TableBodyCell>
    )
}
